import classNames from 'classnames';
import { useComponentInViewport } from 'core-lib/hooks/useComponentInViewport';
import useTimer from 'core-lib/hooks/useTimer';
import { useEffect, useRef } from 'react';
import useStoreItemClick from '../../../hooks/useStoreItemClick';
import { Typography } from 'ui-v2/components/Typography';
import ShimmerImage from 'core-lib/components/ShimmerImage';
import AnimatedImage from 'core-lib/components/AnimatedImage';
import { LayoutEntityType, LayoutPropType } from '../../../types/layout';
import Product from '../../../types/product';
import { trackSectionViewedStoreV2 } from '../../../utils/events';
import s from './GridListing.module.scss';

type PropType = {
  products: Product[];
  listingOrder: number[];
  entityType: LayoutEntityType;
  section?: string;
};
const DisplayItem = ({
  products,
  listingOrder,
  entityType,
  section,
}: PropType) => {
  const getCurrentIndex = (i, j) => {
    const sum = listingOrder.slice(0, i).reduce((acc, num) => acc + num, 0);
    const currentIndex = sum + j;
    return currentIndex;
  };
  const { handleItemClickWithDelegation } = useStoreItemClick();
  const CARDID = entityType;
  var count = 0;
  let res = [];
  for (let i = 0; i < listingOrder?.length; i++) {
    let arr = [];
    for (let j = 0; j < listingOrder[i]; j++) {
      arr.push(
        <div
          className={classNames(s.gridItem, 'cmn_touchable_area')}
          key={count}
          id={CARDID}
          data-index={getCurrentIndex(i, j)}
        >
          <ShimmerImage src={products[count]?.coverImage} className={s.image} />
          <Typography label={products[count]?.title} variant="label_3_medium" />
        </div>
      );
      count++;
    }
    res.push(
      <div
        style={{
          gridTemplateColumns: `repeat(${listingOrder[i]}, 1fr)`,
        }}
        className={s.list}
      >
        {arr.map((item) => item)}
      </div>
    );
  }
  return (
    <div
      onClick={(e) => {
        handleItemClickWithDelegation(e, CARDID, entityType, products, section);
      }}
    >
      {res.map((item) => item)}
    </div>
  );
};

export const GridListing = ({ data }: LayoutPropType) => {
  //constant
  const rootRef = useRef();
  const isEventFired = useRef(false);
  const inViewPort = useComponentInViewport(rootRef, {
    threshold: 0.5,
  });

  const {
    hours: hr,
    minutes: min,
    seconds: sec,
  } = useTimer({
    expiryTimestamp: data?.endTime ? new Date(data?.endTime) : new Date(),
    onExpire: () => {},
  });

  useEffect(() => {
    if (inViewPort && !isEventFired.current) {
      trackSectionViewedStoreV2(data?.title || data?.sectionName);
      isEventFired.current = true;
    }
  }, [data?.sectionName, data?.title, inViewPort]);

  if (!data?.data?.length && !data?.metaData?.listingOrder.length) {
    return null;
  }

  return (
    <div
      className={s.root}
      ref={rootRef}
      style={
        data?.backgroundImage
          ? {
              backgroundImage: `url(${data.backgroundImage})`,
            }
          : {
              background: `${data?.backgroundColor}`,
            }
      }
    >
      <Typography
        label={data?.title}
        variant="label_1_bold"
        className={s.title}
      />
      {data?.metaData?.headingImage && (
        <div className={s.iconWrapper}>
          <AnimatedImage
            src={data?.metaData?.headingImage}
            className={classNames(s.heading, {
              [s.headingHeight]: data?.endTime,
            })}
            alt="headingImage"
          />
        </div>
      )}
      {data?.endTime && (
        <div className={s.timer}>
          <div className={s.textStack}>
            <Typography
              label={`Ends in ${hr < 10 ? `0${hr}h` : `${hr}h`} : ${
                min < 10 ? `0${min}m` : `${min}m`
              } : ${sec < 10 ? `0${sec}s` : `${sec}s`}`}
              variant={'body_3_medium'}
              color="on_dark_bg"
            />
          </div>
        </div>
      )}
      <div className={s.displayCards}>
        <DisplayItem
          products={data?.data}
          listingOrder={data?.metaData?.listingOrder}
          section={data?.sectionName}
          entityType={data?.entityType}
        />
      </div>
    </div>
  );
};
